import SvgIcon from '@/components/svg-icon/svgIcon'
import { Typography } from '@/components/typography/typography'
import { smoothScrollTo } from '@/features/scrolling'
import { useDevice } from '@/hooks/useDevice'
import { cn } from '@/libs/tw'
import { Step, useStepStore } from '@/stores/step-store/stepStore'

type ProgressStepProps = {
	step: Step
	number: string
	title: string
	summary?: string | JSX.Element
	className?: string
	onClick?: () => void
}

export const ProgressStep = (props: ProgressStepProps) => {
	const { isTablet } = useDevice()
	const summarySteps = useStepStore.use.summarySteps()
	const currentStep = useStepStore.use.currentStep()

	const isInSummary = summarySteps.includes(props.step)
	const isCurrentStep = currentStep === props.step
	const showSummary = isInSummary && !isCurrentStep
	const isInactive = !isInSummary && !isCurrentStep

	if (isTablet) return <Desktop {...props} isInactive={isInactive} showSummary={showSummary} />

	return <Mobile {...props} isInactive={isInactive} showSummary={showSummary} />
}

type ResponsiveStepProps = ProgressStepProps & {
	showSummary: boolean
	isInactive: boolean
}

const Desktop = ({
	step,
	className,
	showSummary,
	isInactive,
	number,
	title,
	summary,
}: ResponsiveStepProps) => {
	return (
		<a
			href={`#${step}`}
			data-testid={`bps-${step}`}
			className={cn(
				'flex grow flex-col gap-2 border-r border-solid border-dark-light px-16 xl:basis-[150px] xl:flex-row xl:gap-12 2xl:px-32',
				className
			)}
			onClick={(e) => {
				e.preventDefault()
				return smoothScrollTo(step)
			}}
		>
			{showSummary ? (
				<SvgIcon
					collectionName="ibe"
					iconName="check"
					className="h-[14px] w-[14px] text-progressbar-checkIcon"
				/>
			) : (
				<span
					className={cn(
						'default-font text-[38px] font-progressStep leading-[100%] sm:leading-[16px] lg:text-[38px] lg:leading-[100%]',
						isInactive && 'opacity-50'
					)}
				>
					{number}
				</span>
			)}
			<div className={cn('flex flex-col')}>
				<Typography style="p-s" element="p">
					{title}
				</Typography>

				{showSummary && <Typography style="p-s-bold">{summary}</Typography>}
			</div>
		</a>
	)
}

const Mobile = ({
	step,
	className,
	showSummary,
	isInactive,
	number,
	title,
	summary,
	onClick,
}: ResponsiveStepProps) => {
	return (
		<a
			href={`#${step}`}
			data-testid={`bps-${step}`}
			className={cn(
				'flex items-center justify-between border-t border-solid border-dark-light py-16',
				className
			)}
			onClick={onClick}
		>
			<div className={cn('flex')}>
				{showSummary && (
					<SvgIcon
						collectionName="ibe"
						iconName="check"
						className="h-32 w-32 text-progressbar-checkIcon"
					/>
				)}
				<span
					className={cn(
						'font-default text-[38px] font-[400] leading-[100%] sm:leading-[16px]  lg:text-[38px] lg:leading-[100%]',
						isInactive && 'opacity-50'
					)}
				>
					{number}
				</span>
			</div>
			<div className={cn('flex flex-col items-end font-progressStep')}>
				<Typography style="p" element="p">
					{title}
				</Typography>

				{showSummary && <Typography style="p-bold">{summary}</Typography>}
			</div>
		</a>
	)
}
